import type { ButtonProps } from '@mui/material/Button';
import Button from '@mui/material/Button';
import { Funnel } from '@phosphor-icons/react';
import type { MouseEvent, ReactNode } from 'react';
import { forwardRef, useId, useState } from 'react';
import { Drawer } from '../Drawer/index.ts';
import { Panel } from '../Panel/index.ts';
import { FilterBadge } from './FilterBadge.tsx';
import { useFilterVariantWarning } from './useFilterVariantWarning.ts';

// The `filter` Button variant is registered via module augmentation in FilterTrigger.tsx.

// Default width of the filter drawer's sliding paper. A bare number is a layout param applied
// per-usage via slotProps.paper — there is no single-use width token (HARD RULE 2).
const DEFAULT_DRAWER_WIDTH = 420;

export interface MoreFiltersButtonProps extends Omit<ButtonProps, 'variant' | 'startIcon' | 'children'> {
  /** Trigger label. Defaults to "More Filters". */
  label?: ReactNode;
  /**
   * Number of applied filters (across the additional/drawer filters). When > 0 the trigger shows
   * the active treatment (primary-subtle fill + primary border/text) and a count badge. At 0 there
   * is no badge and no space held for one — the trigger is exactly as wide as its label.
   */
  count?: number;
  /** Force the active treatment independent of `count`. Defaults to `count > 0`. */
  active?: boolean;
  /**
   * Controlled open state of the drawer. Omit to let the component manage open/close itself
   * (uncontrolled — clicking the trigger opens it; backdrop click / Esc / ✕ close it).
   */
  open?: boolean;
  /** Called when the trigger opens the drawer. */
  onOpen?: () => void;
  /** Called when the drawer requests to close (backdrop, Esc, or the ✕). */
  onClose?: () => void;
  /** Drawer body — the additional filter controls. */
  drawerContent?: ReactNode;
  /** Drawer Panel heading. Defaults to `label`. */
  drawerTitle?: ReactNode;
  /** Secondary line under the drawer heading. */
  drawerDescription?: ReactNode;
  /** Primary drawer footer action — e.g. an Apply `Button`. Rendered last in the left-aligned group (Drawer convention). */
  drawerActions?: ReactNode;
  /** Low-emphasis drawer footer action — e.g. a "Clear All" `Button`. Rendered first in the group. */
  drawerSecondaryActions?: ReactNode;
  /** Edge the drawer slides from. Defaults to `'right'` (the common side for a filter sheet). */
  anchor?: 'left' | 'right';
  /** Drawer paper width in px (layout param). Defaults to 420. */
  drawerWidth?: number;
}

/**
 * MoreFiltersButton — a filter-bar trigger that opens a side-sheet Drawer of additional filters
 * (Figma FiltersButtonMore, node 1264:4662). Renders the shared `variant="filter"` Button with a
 * leading Funnel icon (filled when active) and a trailing count badge once filters are applied;
 * clicking it opens a right-anchored `Drawer` hosting the shared `Panel` chrome.
 *
 * Mirrors FilterDropdownButton: it owns the overlay's open/anchor state and ARIA wiring
 * (`aria-haspopup="dialog"`, `aria-expanded`, `aria-controls`), and the leading icon sits in the
 * same h28 "Button Icon" container as the dropdown's caret. It is the one trigger in the family
 * that only ever **counts** — the additional filters are a group, so there is no single value to
 * name. In its default state the badge is absent entirely, with no space held for it (designer,
 * 2026-08-26).
 *
 * Composition — SHARED: `Button`→`ButtonBase`; `Drawer`→`Paper`/`Modal`/`Backdrop`/`Slide` (styled
 * via the Drawer's own slots, never global `MuiPaper`/`MuiModal`). LOCAL: `FilterBadge`. Reused:
 * `Panel` (the shared panel chrome, also hosted by `Dialog`).
 */
export const MoreFiltersButton = forwardRef<HTMLButtonElement, MoreFiltersButtonProps>(function MoreFiltersButton(
  {
    label = 'More Filters',
    count,
    active,
    open,
    onOpen,
    onClose,
    onClick,
    drawerContent,
    drawerTitle,
    drawerDescription,
    drawerActions,
    drawerSecondaryActions,
    anchor = 'right',
    drawerWidth = DEFAULT_DRAWER_WIDTH,
    ...rest
  }: MoreFiltersButtonProps,
  ref
) {
  // This trigger builds its own Button rather than going through FilterTrigger, so it needs
  // the check too — otherwise a bar of More Filters alone would fail silently.
  useFilterVariantWarning();

  const buttonId = useId();
  const drawerId = useId();
  const titleId = useId();

  const isControlled = open !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = isControlled ? open : internalOpen;

  const isActive = active ?? (count != null && count > 0);
  const showBadge = isActive && count != null && count > 0;

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    if (!isControlled) setInternalOpen(true);
    onOpen?.();
    onClick?.(event);
  };

  const handleClose = () => {
    if (!isControlled) setInternalOpen(false);
    onClose?.();
  };

  return (
    <>
      <Button
        ref={ref}
        id={buttonId}
        variant="filter"
        startIcon={<Funnel weight={isActive ? 'fill' : 'regular'} />}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-controls={isOpen ? drawerId : undefined}
        data-active={isActive ? 'true' : undefined}
        onClick={handleOpen}
        {...rest}
      >
        {/* Wrapped so the theme can hang the trailing inset on it — Figma's "Button Text" box
              carries a right padding that differs with and without a badge, and a bare text node
              can't be selected. Same `data-slot` convention as FilterTrigger. */}
        <span data-slot="label">{label}</span>
        {showBadge && <FilterBadge>{count}</FilterBadge>}
      </Button>

      <Drawer
        anchor={anchor}
        open={isOpen}
        onClose={handleClose}
        slotProps={{
          paper: {
            id: drawerId,
            role: 'dialog',
            'aria-modal': true,
            'aria-labelledby': titleId,
            sx: { width: drawerWidth },
          },
        }}
      >
        <Panel
          title={drawerTitle ?? label}
          description={drawerDescription}
          titleId={titleId}
          actions={drawerActions}
          secondaryActions={drawerSecondaryActions}
          footerAlign="left"
          onClose={handleClose}
        >
          {drawerContent}
        </Panel>
      </Drawer>
    </>
  );
});
